import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { Check, FileSearch, Paperclip, Send, Undo2, XCircle } from 'lucide-react'
import { approveInvoice, getInvoice, getInvoiceMatching, invoiceFileUrl, postInvoiceToAP, rejectInvoice, returnInvoiceToPurchasing, runInvoiceMatch, submitInvoice } from '../../api/purchase'
import { useToast } from '../../components/Toast.jsx'
import Button from '../../components/Button'
import Modal from '../../components/Modal'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { fmtDateTime, fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { useSession } from '../../session'
import { money, num } from '../sales/salesShared'
import { invoiceState } from './purchaseShared'
import DefaultAccountSelect, { useDefaultChoices } from '../../components/DefaultAccountSelect'
import { MaterialTag, useMaterialIds } from './itemFor'

const EXCEPTION_LABEL = {
  missing_po: 'Not on the purchase order',
  missing_receipt: 'Nothing received yet',
  price_variance: 'Price differs from the order',
  quantity_variance: 'More than has been received',
  amount_above_po: 'The bills exceed the order',
  freight_discrepancy: 'Freight or extra charges differ',
  missing_account_code: 'An account hasn’t been chosen',
}

// One vendor invoice, from Purchasing's draft through Accounting's checks
// to posting. Shared by /purchase/invoices/:id and /accounting/ap/invoices/:id.
export default function InvoiceDetail() {
  const materialIds = useMaterialIds()
  const { id } = useParams()
  const location = useLocation()
  const viaAccounting = location.pathname.startsWith('/accounting/')
  const { roles, has } = useSession()
  const books = has('accounting') // the plan has Accounting: show accounts and journal entries
  const { notify } = useToast()
  const [hdr, setHdr] = useState(null)
  const [lines, setLines] = useState([])
  const [matching, setMatching] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [dialog, setDialog] = useState(null) // 'return' | 'reject' | 'post'
  // With more than one payables account, posting asks which one.
  const payables = useDefaultChoices('AP')
  const [apAccount, setApAccount] = useState('')
  const [reason, setReason] = useState('')

  const load = async () => {
    try {
      const data = await getInvoice(id)
      setHdr(data.header || null)
      setLines(data.lines || [])
    } catch (e) {
      setHdr(null)
      setError(e.message || 'Could not load this invoice.')
    }
    getInvoiceMatching(id).then(setMatching).catch(() => setMatching(null))
  }

  useEffect(() => { load().finally(() => setLoading(false)) }, [id])

  const run = async (kind, fn, message) => {
    setError('')
    setBusy(kind)
    try {
      const r = await fn()
      notify(typeof message === 'function' ? message(r) : message)
      setDialog(null)
      setReason('')
      await load()
    } catch (e) {
      setError(e.message || 'That did not work. Please try again.')
    } finally {
      setBusy('')
    }
  }

  const isAccounting = roles.includes('accounting') || roles.includes('admin')
  const status = hdr?.status
  const canSubmit = ['Draft', 'ReturnedToPurchasing'].includes(status)
  const canCheck = isAccounting && ['Submitted', 'MatchException'].includes(status)
  const canApprove = isAccounting && status === 'Matched'
  const canPost = isAccounting && status === 'Approved'
  const canReject = isAccounting && ['Submitted', 'Matched', 'MatchException', 'Approved'].includes(status)
  const isService = hdr?.invoice_type === 'NonPO'
  const st = hdr && (viaAccounting && status === 'ReturnedToPurchasing'
    ? { label: 'Sent back to Purchasing', tone: 'warn' }
    : invoiceState(hdr))
  const exceptions = matching?.last_match?.exceptions || []
  const area = viaAccounting ? '/accounting' : '/purchase'
  const lineTotal = lines.reduce((s, l) => s + num(l.line_total || (isService ? l.unit_price : num(l.quantity) * num(l.unit_price))), 0)

  return (
    <div className="ci-form-page">
      <DocHeader
        back={viaAccounting ? <BackLink to="/accounting/ap/matching" label="Vendor invoices" /> : <BackLink to="/purchase/invoices" label="Vendor invoices" />}
        title={hdr?.invoice_number ? `Invoice ${hdr.invoice_number}` : (docNo('bill', hdr?.bill_no) || 'Vendor invoice')}
        badge={st && <StatusBadge label={st.label} tone={st.tone} />}
        meta={hdr && (
          <>
            {hdr.vendor_id ? <Link to={`${area}/ap/vendor/${hdr.vendor_id}`}>{hdr.vendor_name || `Vendor #${hdr.vendor_id}`}</Link> : hdr.vendor_name}
            {hdr.invoice_date && ` · ${fmtDay(hdr.invoice_date)}`}
            {isService ? ' · services' : (
              <>
                {hdr.po_id && <> · <Link to={`/purchase/po-detail/${hdr.po_id}`}>{docNo('po', hdr.po_no)}</Link></>}
                {hdr.receipt_id && <> · <Link to={`/purchase/receipts/${hdr.receipt_id}`}>{docNo('receipt', hdr.receipt_no)}</Link></>}
              </>
            )}
            {hdr.has_file && <> · <a href={invoiceFileUrl(id)} target="_blank" rel="noreferrer"><Paperclip size={12} aria-hidden="true" /> Vendor’s document</a></>}
            {books && hdr.je_id && <> · <Link to={`/accounting/journal/${hdr.je_id}`}>Journal entry {docNo('je', hdr.je_no)}</Link></>}
          </>
        )}
        actions={hdr && (
          <>
            {canReject && <button data-write type="button" className="ci-btn ci-btn-ghost" disabled={!!busy} onClick={() => setDialog('reject')}><XCircle size={14} />Reject</button>}
            {canCheck && <button data-write type="button" className="ci-btn ci-btn-ghost" disabled={!!busy} onClick={() => setDialog('return')}><Undo2 size={14} />Send back to Purchasing</button>}
            {canSubmit && (
              <button data-write type="button" className="ci-btn ci-btn-primary" disabled={!!busy} onClick={() => run('submit', () => submitInvoice(id), 'Sent to Accounting')}>
                <Send size={14} />{busy === 'submit' ? 'Sending…' : 'Send to Accounting'}
              </button>
            )}
            {canCheck && (
              <button data-write type="button" className="ci-btn ci-btn-primary" disabled={!!busy} onClick={() => run('check', () => runInvoiceMatch(id), (r) => (r?.result === 'Matched' ? 'Matches the PO and receipt' : 'Checked: some things don’t match'))}>
                <FileSearch size={14} />{busy === 'check' ? 'Checking…' : 'Check against PO'}
              </button>
            )}
            {canApprove && (
              <button data-write type="button" className="ci-btn ci-btn-primary" disabled={!!busy} onClick={() => run('approve', () => approveInvoice(id), 'Invoice approved')}>
                <Check size={14} />Approve
              </button>
            )}
            {canPost && (
              <button data-write type="button" className="ci-btn ci-btn-primary" disabled={!!busy}
                onClick={() => (payables?.length > 1 ? setDialog('post') : run('post', () => postInvoiceToAP(id), 'Posted to accounts payable'))}>
                <Check size={14} />{busy === 'post' ? 'Posting…' : 'Post to accounts payable'}
              </button>
            )}
          </>
        )}
      />

      {error && <ErrorBanner message={error} />}
      {status === 'ReturnedToPurchasing' && hdr?.return_reason && (
        <p className="doc-note">
          {viaAccounting
            ? <>Sent back to Purchasing: “{hdr.return_reason}”. Waiting for them to fix it and send it again.</>
            : <>Accounting sent this back: “{hdr.return_reason}”. Fix it, then send it to Accounting again.</>}
        </p>
      )}
      {canSubmit && status === 'Draft' && (
        <p className="doc-hint">This is a draft. When it’s right, send it to Accounting to check against the purchase order and pay.</p>
      )}
      {exceptions.length > 0 && (
        <div className="doc-note">
          <strong>Doesn’t match the order:</strong>
          <ul>
            {exceptions.map((e, i) => <li key={i}>{EXCEPTION_LABEL[e.code] || e.code}{e.detail ? `: ${e.detail}` : ''}</li>)}
          </ul>
        </div>
      )}

      {loading ? <Spinner label="Loading invoice…" /> : hdr && (
        <>
          {!isService && matching?.order_lines?.length > 0 && (
            <section className="doc-card">
              <div className="doc-card-head">
                <h2>Against the order</h2>
                <span>Received on every delivery, less returns and earlier bills{matching.tolerance ? ` · allowed difference ${num(matching.tolerance.price_pct)}% on price, ${num(matching.tolerance.qty_pct)}% on quantity` : ''}</span>
              </div>
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead><tr><th>Item</th><th className="num">Ordered</th><th className="num">Received</th><th className="num">Billed before</th><th className="num">This invoice</th><th className="num">Left to bill</th></tr></thead>
                  <tbody>
                    {matching.order_lines.map((o) => {
                      const left = num(o.to_bill) - num(o.this_invoice)
                      return (
                        <tr key={o.product_id} className={left < -0.005 ? 'has-error' : undefined}>
                          <td>{o.name || o.sku}<span className="cell-sub">{o.sku}{num(o.unit_cost) ? ` · ordered at ${money(o.unit_cost)}` : ''}</span></td>
                          <td className="num">{num(o.ordered).toLocaleString()}</td>
                          <td className="num">{(num(o.received) - num(o.returned)).toLocaleString()}{num(o.returned) ? <span className="cell-sub">{num(o.returned)} returned</span> : null}</td>
                          <td className="num">{num(o.billed_before).toLocaleString()}</td>
                          <td className="num">{num(o.this_invoice).toLocaleString()}{num(o.this_price) && num(o.unit_cost) && Math.abs(num(o.this_price) - num(o.unit_cost)) > 0.005 ? <span className="cell-sub">at {money(o.this_price)}</span> : null}</td>
                          <td className={`num${left < -0.005 ? ' ci-negative' : ''}`}>{left < -0.005 ? `${Math.abs(left).toLocaleString()} too many` : left.toLocaleString()}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          <section className="doc-card">
            <div className="doc-card-head">
              <h2>{isService ? 'Charges' : 'Items'}</h2>
              <span>{lines.length} {lines.length === 1 ? 'line' : 'lines'}</span>
            </div>
            <div className="doc-table-scroll">
              <table className="ci-line-table">
                <thead>
                  {isService
                    ? <tr><th>Description</th><th>Account</th><th className="num">Amount</th></tr>
                    : <tr><th>Item</th><th className="num">Quantity</th><th className="num">Unit price</th><th className="num">Amount</th></tr>}
                </thead>
                <tbody>
                  {lines.map((l, i) => (isService ? (
                    <tr key={i}>
                      <td>{l.description}</td>
                      <td>{l.account_code}{l.account_name ? ` · ${l.account_name}` : ''}</td>
                      <td className="num ci-total">{money(l.line_total || l.unit_price)}</td>
                    </tr>
                  ) : (
                    <tr key={i}>
                      <td>{l.name || l.sku}<MaterialTag kind={materialIds.has(Number(l.product_id)) ? 'material' : ''} /><span className="cell-sub">{l.sku}</span></td>
                      <td className="num">{num(l.quantity).toLocaleString()}</td>
                      <td className="num">{money(l.unit_price)}</td>
                      <td className="num ci-total">{money(l.line_total || num(l.quantity) * num(l.unit_price))}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
            <dl className="doc-totals">
              {(num(hdr.tax_amount) > 0 || num(hdr.freight_amount) > 0 || num(hdr.discount_amount) > 0) && (
                <div><dt>{isService ? 'Charges' : 'Items'}</dt><dd>{money(lineTotal)}</dd></div>
              )}
              {num(hdr.tax_amount) > 0 && <div><dt>Tax</dt><dd>{money(hdr.tax_amount)}</dd></div>}
              {num(hdr.freight_amount) > 0 && <div><dt>Freight and other charges</dt><dd>{money(hdr.freight_amount)}</dd></div>}
              {num(hdr.discount_amount) > 0 && <div><dt>Discount</dt><dd>−{money(hdr.discount_amount)}</dd></div>}
              <div className="is-total"><dt>Invoice total</dt><dd>{money(hdr.total_amount)}</dd></div>
            </dl>
          </section>

          {hdr.posted_at && <p className="doc-hint">Posted {fmtDateTime(hdr.posted_at)}.</p>}
        </>
      )}

      <Modal
        open={dialog === 'post'}
        onClose={() => setDialog(null)}
        title="Post to accounts payable"
        subtitle="Choose which of your payables accounts this bill is owed on. Payments and returns for it use the same account."
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button loading={busy === 'post'} onClick={() => run('post', () => postInvoiceToAP(id, apAccount), 'Posted to accounts payable')}>Post</Button>
          </>
        )}
      >
        {books && (
          <label className="ci-form-field">
            <span>Payable account</span>
            <DefaultAccountSelect kinds="AP" accounts={payables || []} value={apAccount} onChange={setApAccount} />
          </label>
        )}
      </Modal>

      <Modal
        open={dialog === 'return'}
        onClose={() => setDialog(null)}
        title="Send back to Purchasing"
        subtitle="Purchasing can fix the invoice and send it again."
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="danger" loading={busy === 'return'} disabled={!reason.trim()} onClick={() => run('return', () => returnInvoiceToPurchasing(id, reason.trim()), 'Sent back to Purchasing')}>Send back</Button>
          </>
        )}
      >
        <label className="ci-form-field">
          <span>What needs fixing?</span>
          <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. The price doesn’t match the PO. Please check with the vendor." />
        </label>
      </Modal>

      <Modal
        open={dialog === 'reject'}
        onClose={() => setDialog(null)}
        title="Reject this invoice"
        subtitle="This can’t be undone. Unlike sending it back, a rejected invoice can’t be edited or sent again."
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="danger" loading={busy === 'reject'} disabled={!reason.trim()} onClick={() => run('reject', () => rejectInvoice(id, reason.trim()), 'Invoice rejected')}>Reject</Button>
          </>
        )}
      >
        <label className="ci-form-field">
          <span>Why?</span>
          <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Duplicate of an invoice already entered." />
        </label>
      </Modal>
    </div>
  )
}
